import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Payment, ConfirmationNumber } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface Campaign {
  id: number;
  name: string;
  status: string;
  created_at: string;
}

const TopUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [amount, setAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<'sbp' | 'card' | 'promocode' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<number | ''>('');
  const [promocode, setPromocode] = useState<string>('');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [promocodeLoading, setPromocodeLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Получаем сумму и ID кампании из URL параметров и загружаем кампании при загрузке страницы
  useEffect(() => {
    const amountFromUrl = searchParams.get('amount');
    const campaignIdFromUrl = searchParams.get('campaign_id');
    
    if (amountFromUrl) {
      setAmount(amountFromUrl);
    }
    
    if (campaignIdFromUrl) {
      setSelectedCampaign(parseInt(campaignIdFromUrl));
    }
    
    // Загружаем кампании пользователя
    if (user?.id) {
      loadUserCampaigns();
    }
  }, [searchParams, user]);

  const loadUserCampaigns = async () => {
    try {
      const response = await fetch(`/api/promocodes/campaigns?user_id=${user?.id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Ошибка загрузки кампаний:', error);
    }
  };

  const handleAmountChange = (value: string) => {
    // Разрешаем только цифры и точку
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    setAmount(sanitizedValue);
    setError('');
  };

  const handlePaymentMethodSelect = (method: 'sbp' | 'card' | 'promocode') => {
    setPaymentMethod(method);
    setError('');
    setSuccess('');
    
    // Если выбран промокод, очищаем поле суммы
    if (method === 'promocode') {
      setAmount('');
    }
  };

  const checkPromocode = async () => {
    if (!promocode.trim()) {
      setError('Введите промокод');
      return;
    }

    setPromocodeLoading(true);
    setError('');

    try {
      const response = await fetch('/api/promocodes/check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: promocode.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setAmount(data.amount.toString());
        setSuccess(`Промокод действителен! Сумма: ${data.amount}₽`);
      } else {
        setError(data.message || 'Промокод недействителен');
      }
    } catch (error) {
      setError('Ошибка проверки промокода');
    } finally {
      setPromocodeLoading(false);
    }
  };

  const applyPromocode = async () => {
    if (!user?.id || !selectedCampaign || !promocode.trim()) {
      setError('Заполните все поля');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/promocodes/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: promocode.trim(),
          user_id: user.id,
          campaign_id: selectedCampaign,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`Промокод успешно применен! Баланс пополнен на ${data.amount}₽`);
        setPromocode('');
        setAmount('');
      } else {
        setError(data.message || 'Ошибка применения промокода');
      }
    } catch (error) {
      setError('Ошибка применения промокода');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUp = () => {
    // Валидация
    if (!selectedCampaign) {
      setError('Выберите кампанию');
      return;
    }

    if (!paymentMethod) {
      setError('Выберите способ оплаты');
      return;
    }

    if (paymentMethod === 'promocode') {
      applyPromocode();
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Введите корректную сумму');
      return;
    }

    // Для СБП и карты пока что редиректим на alfabank.ru
    window.open('https://alfabank.ru', '_blank');
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          onClick={handleBack}
          sx={{ 
            minWidth: 48, 
            width: 48, 
            height: 48, 
            color: '#000000',
            p: 0,
            mr: 2
          }}
        >
          <ArrowBack />
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#000000' }}>
          Пополнение баланса
        </Typography>
      </Box>

      {/* Main Form */}
      <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {/* Campaign Selector */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000000' }}>
              Кампания *
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value as number)}
                displayEmpty
                sx={{
                  bgcolor: 'rgb(248, 242, 255)',
                  borderRadius: '36px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem value="">
                  <em>Выберите кампанию</em>
                </MenuItem>
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {error && !selectedCampaign && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                Выберите кампанию
              </Typography>
            )}
          </Box>

          {/* Amount Field */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000000' }}>
              {paymentMethod === 'promocode' ? 'Сумма (автоматически)' : 'Сумма пополнения'}
            </Typography>
            <Box
              sx={{
                bgcolor: 'rgb(248, 242, 255)',
                borderRadius: '36px',
                p: 2,
                border: error && (!amount || parseFloat(amount) <= 0) ? '1px solid #f44336' : 'none',
              }}
            >
              <input
                type="text"
                placeholder={paymentMethod === 'promocode' ? 'Сумма определится автоматически' : 'Введите сумму в рублях'}
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                disabled={paymentMethod === 'promocode'}
                style={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: paymentMethod === 'promocode' ? '#999' : '#000000',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </Box>
            {error && (!amount || parseFloat(amount) <= 0) && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                Введите корректную сумму
              </Typography>
            )}
          </Box>

          {/* Payment Method */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000000' }}>
              Способ оплаты
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="СБП"
                variant={paymentMethod === 'sbp' ? "filled" : "outlined"}
                onClick={() => handlePaymentMethodSelect('sbp')}
                sx={{
                  borderColor: paymentMethod === 'sbp' ? '#000000' : '#ececfc',
                  border: paymentMethod === 'sbp' ? '1px solid #000000' : '1px solid #ececfc',
                  color: paymentMethod === 'sbp' ? '#ffffff' : '#514b81',
                  bgcolor: paymentMethod === 'sbp' ? '#000000' : '#ffffff',
                  '&:hover': {
                    bgcolor: paymentMethod === 'sbp' ? '#000000' : 'rgba(0, 0, 0, 0.04)',
                  },
                  cursor: 'pointer',
                  px: 2,
                  py: 1,
                }}
              />
              <Chip
                label="Банковская карта"
                variant={paymentMethod === 'card' ? "filled" : "outlined"}
                onClick={() => handlePaymentMethodSelect('card')}
                sx={{
                  borderColor: paymentMethod === 'card' ? '#000000' : '#ececfc',
                  border: paymentMethod === 'card' ? '1px solid #000000' : '1px solid #ececfc',
                  color: paymentMethod === 'card' ? '#ffffff' : '#514b81',
                  bgcolor: paymentMethod === 'card' ? '#000000' : '#ffffff',
                  '&:hover': {
                    bgcolor: paymentMethod === 'card' ? '#000000' : 'rgba(0, 0, 0, 0.04)',
                  },
                  cursor: 'pointer',
                  px: 2,
                  py: 1,
                }}
              />
              <Chip
                label="Промокод"
                variant={paymentMethod === 'promocode' ? "filled" : "outlined"}
                onClick={() => handlePaymentMethodSelect('promocode')}
                sx={{
                  borderColor: paymentMethod === 'promocode' ? '#000000' : '#ececfc',
                  border: paymentMethod === 'promocode' ? '1px solid #000000' : '1px solid #ececfc',
                  color: paymentMethod === 'promocode' ? '#ffffff' : '#514b81',
                  bgcolor: paymentMethod === 'promocode' ? '#000000' : '#ffffff',
                  '&:hover': {
                    bgcolor: paymentMethod === 'promocode' ? '#000000' : 'rgba(0, 0, 0, 0.04)',
                  },
                  cursor: 'pointer',
                  px: 2,
                  py: 1,
                }}
              />
            </Box>
            {error && !paymentMethod && (
              <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block' }}>
                Выберите способ оплаты
              </Typography>
            )}
          </Box>

          {/* Promocode Field */}
          {paymentMethod === 'promocode' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#000000' }}>
                Промокод
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box
                  sx={{
                    bgcolor: 'rgb(248, 242, 255)',
                    borderRadius: '36px',
                    p: 2,
                    flex: 1,
                  }}
                >
                  <input
                    type="text"
                    placeholder="Введите промокод"
                    value={promocode}
                    onChange={(e) => setPromocode(e.target.value.toUpperCase())}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: '#000000',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  onClick={checkPromocode}
                  disabled={promocodeLoading || !promocode.trim()}
                  sx={{
                    borderRadius: '36px',
                    px: 3,
                    borderColor: '#000000',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#000000',
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {promocodeLoading ? <CircularProgress size={20} /> : 'Проверить'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Top Up Button */}
          <Button
            variant="contained"
            fullWidth
            startIcon={paymentMethod === 'promocode' ? <ConfirmationNumber /> : <Payment />}
            onClick={handleTopUp}
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
              color: '#ffffff !important',
              boxShadow: 'none',
              fontWeight: 700,
              '&:hover': { 
                background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
                opacity: 0.9,
                boxShadow: 'none'
              },
              '&:disabled': {
                background: '#ccc',
                color: '#666 !important',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : paymentMethod === 'promocode' ? (
              'Применить промокод'
            ) : (
              'Пополнить'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000' }}>
            Информация о пополнении
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Минимальная сумма пополнения: 100₽
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • Средства поступают на баланс мгновенно
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            • Комиссия за пополнение не взимается
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TopUpPage;
